import { Card, ConfirmDialog, useToast } from '@citizen-walk/shared-ui';
import type {
  AiVerificationAssignmentPlan,
  AiVerificationAssignmentSet,
} from '@citizen-walk/shared-types';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAdminAuth } from '../auth';
import { useDistrict } from '../districts-admin';
import { useSessions } from '../sessions-admin';
import { AssignmentPlanList } from './AssignmentPlanList';
import { RunPlanDialog } from './RunPlanDialog';
import { deleteAssignmentPlan, useAssignmentPlans } from './useAssignmentPlans';
import { setAssignmentSetStatus } from './useAssignmentSetEditor';
import { useDeleteAssignmentSet } from './useDeleteAssignmentSet';
import { useAdminAssignmentSets } from './useVerificationResults';
import { useVerificationTemplates } from './useVerificationTemplates';

/**
 * VerificationAssignmentsPage (Sprint BG-DataSettings 10、Phase 5、GAP A-S21)。
 *
 * 配分セット一覧 + 新規作成 link + 削除 (master/district admin)。
 * 旧 `old/apps/admin/src/pages/VerificationAssignmentsPage.tsx` を v2 に踏襲しつつ、
 * 「新規作成」は callable (generateAssignments) を呼ぶ専用 edit page に link するだけに変更。
 *
 * 旧との差分:
 *   - listAssignmentSets (one-shot) → useAdminAssignmentSets (onSnapshot reactive)
 *   - 新規作成は本 page で空 doc を作る → 編集 page (callable 呼び出し UI) に link
 *   - 削除は ConfirmDialog 経由 (旧 confirm() を v2 design system に置換)
 *   - 状態 badge / distribution badge は AiVerificationAssignmentSet の DIST_LABELS で
 *
 * 2026-09-04:
 *   - 上段に「配分の予約」(AssignmentPlanList + RunPlanDialog) を追加。イベント前に配り方を保存し、
 *     セッション終了後に「▶ 配分する」で投稿・参加者を自動収集して配分 (再実行は作り直し)。
 *   - 配分セットの削除をクライアント deleteDoc (rules で常に失敗していた) から callable
 *     `deleteAssignmentSet` (assignments ごと削除) に置換。回答 (posts/{id}/verifications) は残る。
 *   - サイドバー「検証の配分」の着地点になった (旧 /verification/distribute から付け替え)。
 */

const DIST_LABELS: Record<string, string> = {
  all_same: '全員同一',
  random_per_user: 'ランダム配分',
  manual: '個別アサイン',
  free: '自由検証',
};

const STATUS_LABELS: Record<string, string> = {
  draft: '下書き',
  published: '公開中',
  closed: 'クローズ',
};

const STATUS_BADGE: Record<string, string> = {
  draft: 'bg-slate-300 text-slate-800',
  published: 'bg-success-600 text-white',
  closed: 'bg-slate-500 text-white',
};

export function VerificationAssignmentsPage() {
  const { districtId } = useParams<{ districtId: string }>();
  const { state: authState } = useAdminAuth();
  const districtState = useDistrict(districtId ?? null);
  const templatesState = useVerificationTemplates(districtId ?? null);
  const setsState = useAdminAssignmentSets(districtId ?? null);
  const plansState = useAssignmentPlans(districtId ?? null);
  const sessionsState = useSessions(districtId ?? '');
  const { call: callDeleteSet, state: deleteSetState } = useDeleteAssignmentSet();
  const { toast } = useToast();
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [statusBusyId, setStatusBusyId] = useState<string | null>(null);
  // 配分の予約: 実行ダイアログ / 削除確認
  const [runTarget, setRunTarget] = useState<{
    plan: AiVerificationAssignmentPlan;
    sessionIds?: string[];
  } | null>(null);
  const [deletePlanTarget, setDeletePlanTarget] = useState<AiVerificationAssignmentPlan | null>(
    null,
  );
  const [deletingPlan, setDeletingPlan] = useState(false);

  const isMaster = authState.role === 'master';
  const isDistrictAdminOfThis =
    authState.role === 'district' && !!districtId && authState.districts.includes(districtId);
  const canManage = isMaster || isDistrictAdminOfThis;
  const actorUid = authState.user?.uid ?? '';

  // docs/108 Critical #12 — 一覧から status (下書き/公開中/クローズ) を直接切替える row アクション。
  async function handleStatusChange(setId: string, next: AiVerificationAssignmentSet['status']) {
    if (!districtId || !canManage) return;
    setStatusBusyId(setId);
    try {
      await setAssignmentSetStatus(districtId, setId, next, actorUid);
      toast('success', `公開状態を「${STATUS_LABELS[next] ?? next}」に変更しました`);
    } catch (e) {
      toast('error', `状態変更失敗: ${(e as Error).message}`);
    } finally {
      setStatusBusyId(null);
    }
  }

  const sets = setsState.status === 'ready' ? setsState.items : [];
  const templates = templatesState.status === 'ready' ? templatesState.items : [];
  const deleteTarget = sets.find((s) => s.id === deleteTargetId) ?? null;

  // 2026-09-04: rules はクライアント直 delete を禁止 (assignments が孤児になる) → callable で assignments ごと削除。
  async function handleDelete() {
    if (!districtId || !deleteTargetId) return;
    setDeleting(true);
    const res = await callDeleteSet({ districtId, setId: deleteTargetId });
    setDeleting(false);
    if (res) {
      toast('success', '配分セットを削除しました');
      setDeleteTargetId(null);
    } else {
      toast('error', `削除に失敗しました: ${deleteSetState.error?.message ?? '不明なエラー'}`);
    }
  }

  async function handleDeletePlan() {
    if (!districtId || !deletePlanTarget) return;
    setDeletingPlan(true);
    try {
      await deleteAssignmentPlan(districtId, deletePlanTarget.id);
      toast('success', '予約を削除しました');
      setDeletePlanTarget(null);
    } catch (e) {
      toast('error', `削除に失敗しました: ${(e as Error).message}`);
    } finally {
      setDeletingPlan(false);
    }
  }

  const plans = plansState.status === 'ready' ? plansState.items : [];
  const sessions = sessionsState.status === 'ready' ? sessionsState.items : [];

  return (
    <>
      <header className="mb-6">
        <Link
          to={districtId ? `/districts/${districtId}` : '/districts'}
          className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
        >
          ← 地区詳細
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 mt-2">
          検証の配分
          {districtState.status === 'ready' ? ` — ${districtState.district.name}` : ''}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          AI
          の解析結果をだれに確認してもらうかを決めて配ります。イベント前に「配分の予約」で配り方を決めておくと、
          セッション終了後にボタン 1 つで配分できます。投稿を選んでその場で配ることもできます。
        </p>
      </header>

      {!canManage && (
        <Card padding="md" className="border-danger-600 mb-4">
          <p className="text-sm text-danger-700">この地区の配分を管理する権限がありません。</p>
        </Card>
      )}

      {/* 2026-09-04 — 配分の予約 (セッション終了後にワンクリック配分) */}
      <section className="mb-8" aria-labelledby="assignment-plans-heading">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 id="assignment-plans-heading" className="text-base font-bold text-slate-900">
            配分の予約
            <span className="ml-2 text-xs font-normal text-slate-500">
              配り方を先に決めておき、セッション終了後に実行
            </span>
          </h2>
          <Link
            to={`/districts/${districtId}/verification/plans/new`}
            aria-disabled={!canManage}
            className={`inline-flex items-center gap-1 min-h-touch px-4 rounded text-sm font-medium ${
              canManage
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'bg-slate-300 text-slate-500 pointer-events-none'
            }`}
          >
            + 予約を作る
          </Link>
        </div>
        {plansState.status === 'loading' && (
          <Card padding="md">
            <p className="text-sm text-slate-600">読込中…</p>
          </Card>
        )}
        {plansState.status === 'error' && (
          <Card padding="md" className="border-danger-600">
            <p className="text-sm text-danger-700">読込エラー: {plansState.error.message}</p>
          </Card>
        )}
        {plansState.status === 'ready' && districtId && (
          <AssignmentPlanList
            districtId={districtId}
            plans={plans}
            templates={templates}
            sessions={sessions}
            canManage={canManage}
            onRun={(plan, sessionIds) =>
              setRunTarget({ plan, ...(sessionIds ? { sessionIds } : {}) })
            }
            onDelete={(plan) => setDeletePlanTarget(plan)}
          />
        )}
      </section>

      <h2 className="text-base font-bold text-slate-900 mb-3">
        配分セット
        <span className="ml-2 text-xs font-normal text-slate-500">
          予約から作られたものと、投稿を選んで作ったものの一覧
        </span>
      </h2>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-sm text-slate-600">
          {sets.length} 件
          {templates.length === 0 && canManage && (
            <span className="ml-2 text-xs text-warning-700">
              ⚠ テンプレートがありません。先に
              <Link
                to={`/districts/${districtId}/verification/templates`}
                className="ml-1 text-primary-700 underline"
              >
                テンプレート
              </Link>
              を作成してください。
            </span>
          )}
        </p>
        <Link
          to={`/districts/${districtId}/verification/assignmentSets/new`}
          aria-disabled={!canManage}
          className={`inline-flex items-center gap-1 min-h-touch px-4 rounded text-sm font-medium ${
            canManage
              ? 'bg-primary-600 text-white hover:bg-primary-700'
              : 'bg-slate-300 text-slate-500 pointer-events-none'
          }`}
        >
          + 新規セット
        </Link>
      </div>

      {setsState.status === 'loading' && (
        <Card padding="md">
          <p className="text-sm text-slate-600">読込中…</p>
        </Card>
      )}
      {setsState.status === 'error' && (
        <Card padding="md" className="border-danger-600">
          <p className="text-sm text-danger-700">読込エラー: {setsState.error.message}</p>
        </Card>
      )}
      {setsState.status === 'ready' && sets.length === 0 && (
        <Card padding="md">
          <p className="text-sm text-slate-600">配分セットはまだありません。</p>
        </Card>
      )}
      {setsState.status === 'ready' && sets.length > 0 && (
        <ul className="grid grid-cols-1 gap-2" data-testid="assignment-sets-list">
          {sets.map((s) => {
            const tpl = templates.find((t) => t.id === s.templateId);
            return (
              <li key={s.id}>
                <Card padding="md">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded ${STATUS_BADGE[s.status] ?? ''}`}
                        >
                          {STATUS_LABELS[s.status] ?? s.status}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-100 text-primary-800">
                          {DIST_LABELS[s.distribution] ?? s.distribution}
                        </span>
                        <strong className="text-sm text-slate-900">
                          {s.displayName || '(無題)'}
                        </strong>
                      </div>
                      <p className="text-xs text-slate-500">
                        テンプレ: {tpl?.displayName ?? s.templateId} (v{s.templateVersion}) · 候補{' '}
                        {s.candidatePostIds.length} 件
                      </p>
                      {s.status !== 'published' && (
                        <p className="mt-1 text-[11px] text-danger-700">
                          ⚠ 公開中にしないと参加者に表示されません
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {/* docs/108 Critical #12 — 公開状態の row 切替 */}
                      <select
                        value={s.status}
                        onChange={(e) =>
                          void handleStatusChange(
                            s.id,
                            e.target.value as AiVerificationAssignmentSet['status'],
                          )
                        }
                        disabled={!canManage || statusBusyId === s.id}
                        aria-label={`${s.displayName || '(無題)'} の公開状態`}
                        className={`text-xs px-2 min-h-touch border rounded bg-white ${
                          s.status === 'published'
                            ? 'border-success-500 bg-success-50'
                            : 'border-slate-300'
                        }`}
                      >
                        <option value="draft">下書き</option>
                        <option value="published">公開中</option>
                        <option value="closed">クローズ</option>
                      </select>
                      <Link
                        to={`/districts/${districtId}/verification/assignmentSets/${s.id}/edit`}
                        className="text-xs text-primary-700 hover:underline"
                      >
                        編集
                      </Link>
                      <button
                        type="button"
                        onClick={() => setDeleteTargetId(s.id)}
                        disabled={!canManage}
                        className="text-xs text-danger-700 hover:underline disabled:opacity-40"
                      >
                        削除
                      </button>
                    </div>
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmDialog
        open={!!deleteTargetId}
        title="配分セットを削除しますか?"
        message={
          deleteTarget
            ? `「${deleteTarget.displayName || '(無題)'}」を削除します。この回の配分記録（だれにどの投稿を配ったか）が消えます。参加者が入力した検証の回答は残ります。`
            : ''
        }
        confirmLabel={deleting ? '削除中…' : '削除'}
        cancelLabel="キャンセル"
        intent="danger"
        onConfirm={() => void handleDelete()}
        onCancel={() => setDeleteTargetId(null)}
      />

      <ConfirmDialog
        open={!!deletePlanTarget}
        title="予約を削除しますか?"
        message={
          deletePlanTarget
            ? `「${deletePlanTarget.displayName || '(無題)'}」を削除します。この予約から作られた配分セットは残ります。`
            : ''
        }
        confirmLabel={deletingPlan ? '削除中…' : '削除'}
        cancelLabel="キャンセル"
        intent="danger"
        onConfirm={() => void handleDeletePlan()}
        onCancel={() => setDeletePlanTarget(null)}
      />

      {districtId && (
        <RunPlanDialog
          // 開くたびに remount して phase / 実行結果を初期化する (effect 内 setState を避ける)
          key={
            runTarget ? `${runTarget.plan.id}:${(runTarget.sessionIds ?? []).join('|')}` : 'closed'
          }
          open={!!runTarget}
          districtId={districtId}
          plan={runTarget?.plan ?? null}
          {...(runTarget?.sessionIds ? { sessionIds: runTarget.sessionIds } : {})}
          onClose={() => setRunTarget(null)}
          onExecuted={(res) => {
            const ok = res.targets.filter((t) => t.status === 'ok');
            const total = ok.reduce((a, t) => a + t.assignmentCount, 0);
            if (ok.length > 0) {
              toast('success', `配分しました（${ok.length} 件の対象、合計 ${total} 件）`);
            } else {
              toast('error', '配分できた対象がありませんでした');
            }
          }}
        />
      )}
    </>
  );
}
